/**
 * Wzory materiałów: wzory z aplikacji (layouts.js, klucz 'builtin:0') i wzory zakładu
 * (zapisane w bazie, klucz 'pattern:15'). Wzory są przypisane do rodzaju materiału.
 *
 * Wzór zakładu to różnice układu (format z layouts/fabric/layoutChanges.js) względem wzoru
 * z aplikacji `baseVariant`. Poprawki konkretnego zmarłego nakładają się na wzór.
 */

import { useEffect, useState } from 'react';
import axios from '../../../axios';
import layouts from '../layouts/layouts';

export const PATTERN_LIMIT = 8;

const VARIANT_KEY_REGEX = /^(builtin|pattern):(\d+)$/;

export const parseVariantKey = (key) => {
    const match = VARIANT_KEY_REGEX.exec(key || '');
    return match ? { kind: match[1], id: Number(match[2]) } : null;
};

/** Wzór z adresu (variant), a dla starszych linków - numer wariantu (selectedLayoutTypeType) */
export const getVariantKeyFromParams = (params) => {
    const variant = params.get('variant');
    if (parseVariantKey(variant)) return variant;
    const legacyIndex = Number(params.get('selectedLayoutTypeType') || 0);
    return `builtin:${Number.isInteger(legacyIndex) && legacyIndex >= 0 ? legacyIndex : 0}`;
};

/** Wzory z aplikacji dla rodzaju materiału */
export const getBuiltinVariants = layoutType => (
    (layouts[layoutType]?.params || []).map((param, index) => ({
        key: `builtin:${index}`,
        name: param.typeName || 'Standardowy',
        baseVariant: index,
        builtin: true,
    }))
);

/** Ulubione na początku, reszta w dotychczasowej kolejności (jak przy ramkach) */
export const sortVariantsByFavorites = (variants, favoriteKeys) => {
    const favorites = favoriteKeys instanceof Set ? favoriteKeys : new Set(favoriteKeys);
    return variants
        .map((variant, originalIndex) => ({ variant, originalIndex }))
        .sort((first, second) => {
            const firstIsFavorite = favorites.has(first.variant.key);
            const secondIsFavorite = favorites.has(second.variant.key);
            if (firstIsFavorite !== secondIsFavorite) return firstIsFavorite ? -1 : 1;
            return first.originalIndex - second.originalIndex;
        })
        .map(({ variant }) => variant);
};

export async function fetchPatternsForType(layoutType) {
    const response = await axios.get('/patterns', { params: { layoutType } });
    return {
        patterns: response.data?.patterns || [],
        favorites: response.data?.favorites || [],
        limit: response.data?.limit || PATTERN_LIMIT,
    };
}

export async function fetchPattern(id) {
    const response = await axios.get(`/patterns/${id}`);
    return response.data;
}

export async function createPattern({ layoutType, baseVariant, name, layoutChanges }) {
    const response = await axios.post('/patterns', { layoutType, baseVariant, name, layoutChanges });
    return response.data;
}

export async function updatePattern(id, changes) {
    const response = await axios.put(`/patterns/${id}`, changes);
    return response.data;
}

export function deletePattern(id) {
    return axios.delete(`/patterns/${id}`);
}

export function setPatternFavorite(layoutType, patternKey, isFavorite) {
    const url = `/pattern-favorites/${layoutType}/${encodeURIComponent(patternKey)}`;
    return isFavorite ? axios.put(url) : axios.delete(url);
}

/**
 * Wzór do narysowania materiału: wzór z aplikacji (baseVariant) + ewentualne zmiany wzoru zakładu.
 * Wzór zakładu pobieramy po id - także usunięty, bo materiały na nim zrobione nadal muszą się wyświetlać.
 */
export function useResolvedVariant(variantKey) {
    const parsed = parseVariantKey(variantKey);
    const [state, setState] = useState(() => (
        parsed?.kind === 'pattern'
            ? { loaded: false, baseVariant: 0, pattern: null }
            : { loaded: true, baseVariant: parsed?.id || 0, pattern: null }
    ));

    useEffect(() => {
        let cancelled = false;
        const current = parseVariantKey(variantKey);
        if (current?.kind !== 'pattern') {
            setState({ loaded: true, baseVariant: current?.id || 0, pattern: null });
            return undefined;
        }

        setState(previous => ({ ...previous, loaded: false }));
        fetchPattern(current.id)
            .then((pattern) => {
                if (!cancelled) setState({ loaded: true, baseVariant: pattern.baseVariant || 0, pattern });
            })
            .catch((error) => {
                console.error('Nie udało się wczytać wzoru:', error);
                // Bez wzoru rysujemy wersję z aplikacji - materiał nadal da się pobrać
                if (!cancelled) setState({ loaded: true, baseVariant: 0, pattern: null });
            });
        return () => { cancelled = true; };
    }, [variantKey]);

    return state;
}
